import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { eventService } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import MessagesSidebar from '../components/MessagesSidebar';

const EventForm = () => {
    const { slug } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { language } = useLanguage();

    // Query params
    const formType = searchParams.get('type') || '0';
    const isViewMode = searchParams.get('view') === 'true';
    const isEditMode = searchParams.get('edit') === 'true';
    const isReadOnly = isViewMode && !isEditMode;

    // Dropdown menu state
    const [activeMenu, setActiveMenu] = useState(null);
    const [activeSubMenu, setActiveSubMenu] = useState(null);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // Adım yönetimi
    const [currentStep, setCurrentStep] = useState(0);
    const [completedSteps, setCompletedSteps] = useState([]);
    const [formValues, setFormValues] = useState({});
    const [filePreviews, setFilePreviews] = useState({});
    const [files, setFiles] = useState({});

    // API state
    const [formData, setFormData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // Save/Submit state
    const [saveMessage, setSaveMessage] = useState('');
    const [submitError, setSubmitError] = useState(null);
    const [submitSuccess, setSubmitSuccess] = useState(null);

    const totalSteps = formData?.groups?.length || 0;
    const progress = totalSteps > 0 ? Math.round((completedSteps.length / totalSteps) * 100) : 0;
    const isLastStep = currentStep === totalSteps - 1;

    const menuItems = [
        {
            id: 1,
            title: language === 'fa' ? 'ثبت نام در نمایشگاه' : 'Exhibition Registration',
            hasSubmenu: true,
            subItems: [language === 'fa' ? 'امور مالی' : 'Financial Affairs', language === 'fa' ? 'پیام ها' : 'Messages']
        },
        { id: 2, title: language === 'fa' ? 'حساب کاربری' : 'User Account', hasSubmenu: false },
        { id: 3, title: language === 'fa' ? 'پشتیبانی' : 'Support', hasSubmenu: false },
        { id: 4, title: language === 'fa' ? 'خروج از حساب کاربری' : 'Logout', hasSubmenu: false }
    ];

    const storageKey = `event_form_${slug}_${formType}`;

    useEffect(() => {
        fetchFormData();
    }, [slug, language, formType]);

    useEffect(() => {
        if (formData && !loading && !isReadOnly) {
            loadSavedData();
        }
    }, [formData, loading]);

    const fetchFormData = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await eventService.getEventForm(slug || 'test11111', formType);
            const sortedGroups = (data.groups || []).sort((a, b) => a.step - b.step);
            setFormData({ ...data, groups: sortedGroups });

            if (data.groups) {
                const values = {};
                const previews = {};
                for (const group of data.groups) {
                    for (const field of group.fields) {
                        const key = `${group.slug}.${field.slug}`;
                        if (field.value) {
                            values[key] = field.value;
                        }
                        if (field.file) {
                            previews[key] = field.file;
                            values[key] = field.file.split('/').pop() || field.file;
                        }
                    }
                }
                setFormValues(values);
                setFilePreviews(previews);
            }
        } catch (err) {
            console.error('Form API error:', err);
            if (err.response?.data?.code === 'translation_missing') {
                setError(language === 'fa' ? 'ترجمه در دسترس نیست' : 'Translation not available');
            } else {
                setError(language === 'fa' ? 'خطا در دریافت فرم' : 'Error fetching form');
            }
        } finally {
            setLoading(false);
        }
    };

    const loadSavedData = () => {
        try {
            const saved = localStorage.getItem(storageKey);
            if (saved) {
                const parsed = JSON.parse(saved);
                if (parsed.formValues) setFormValues(parsed.formValues);
                if (parsed.completedSteps) setCompletedSteps(parsed.completedSteps);
                if (parsed.currentStep !== undefined) setCurrentStep(parsed.currentStep);
                if (parsed.filePreviews) setFilePreviews(parsed.filePreviews);
                setSaveMessage(language === 'fa' ? 'اطلاعات قبلی بازیابی شد' : 'Previous data restored');
                setTimeout(() => setSaveMessage(''), 3000);
            }
        } catch (err) {
            console.error('Error loading saved data:', err);
        }
    };

    const saveToLocalStorage = () => {
        try {
            const dataToSave = { formValues, completedSteps, currentStep, filePreviews, savedAt: new Date().toISOString() };
            localStorage.setItem(storageKey, JSON.stringify(dataToSave));
            setSaveMessage(language === 'fa' ? 'اطلاعات با موفقیت ذخیره شد' : 'Data saved successfully');
            setTimeout(() => setSaveMessage(''), 3000);
        } catch (err) {
            console.error('Error saving data:', err);
        }
    };

    const areAllRequiredFieldsFilled = () => {
        if (!formData?.groups) return false;
        for (const group of formData.groups) {
            for (const field of group.fields) {
                if (field.required && field.type !== 2) {
                    const key = `${group.slug}.${field.slug}`;
                    if (!formValues[key] || formValues[key].toString().trim() === '') return false;
                }
                if (field.required && field.type === 2) {
                    const key = `${group.slug}.${field.slug}`;
                    if (!files[key] && !filePreviews[key]) return false;
                }
            }
        }
        return true;
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        setSubmitError(null);
        setSubmitSuccess(null);

        try {
            const formDataToSend = new FormData();

            const dataPayload = {
                groups: formData.groups.map(group => ({
                    slug: group.slug,
                    fields: group.fields.map(field => {
                        const key = `${group.slug}.${field.slug}`;
                        if (field.type === 2) {
                            // Yeni dosya varsa ekle, yoksa mevcut dosyayı koru (null dön)
                            if (files[key]) return { slug: field.slug };
                            return null;
                        }
                        return {
                            slug: field.slug,
                            value: field.type === 1 ? parseInt(formValues[key]) || 0 : formValues[key] || ''
                        };
                    }).filter(f => f !== null)
                })).filter(group => group.fields.length > 0)
            };

            formDataToSend.append('form', JSON.stringify(dataPayload));

            // Sadece yeni yüklenen dosyaları ekle
            for (const group of formData.groups) {
                for (const field of group.fields) {
                    if (field.type === 2) {
                        const key = `${group.slug}.${field.slug}`;
                        if (files[key]) {
                            formDataToSend.append(field.slug, files[key]);
                        }
                    }
                }
            }

            let response;

            if (isEditMode) {
                response = await eventService.updateEventForm(slug, formDataToSend, formType);
            } else {
                response = await eventService.submitEventForm(slug, formDataToSend, formType);
            }

            setSubmitSuccess(response.message || (language === 'fa'
                ? (isEditMode ? 'اطلاعات با موفقیت بروزرسانی شد' : 'ثبت‌نام با موفقیت انجام شد')
                : (isEditMode ? 'Successfully updated' : 'Registration successful')
            ));
            localStorage.removeItem(storageKey);

            setTimeout(() => navigate(`/events/${slug}`), 2000);

        } catch (err) {
            console.error('Submit error:', err);
            if (err.response?.data) {
                const data = err.response.data;
                if (data.code === 'form_already_submitted') setSubmitError(data.message);
                else if (data.code === 'capacity_full') setSubmitError(data.message);
                else if (data.code === 'submission_not_found') setSubmitError(data.message);
                else if (data.code === 'cannot_edit') setSubmitError(data.message);
                else if (data.errors) setSubmitError(data.errors.map(e => e.message).join('\n'));
                else if (data.detail) setSubmitError(data.detail);
                else setSubmitError(language === 'fa' ? 'خطا در ثبت‌نام' : 'Registration error');
            } else {
                setSubmitError(language === 'fa' ? 'خطا در ارتباط با سرور' : 'Server connection error');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleMenuClick = (menuId) => { setActiveMenu(activeMenu === menuId ? null : menuId); setActiveSubMenu(null); };
    const handleSubMenuClick = (subIndex) => { setActiveSubMenu(subIndex); };

    const handleInputChange = (groupSlug, fieldSlug, value) => {
        if (isReadOnly) return;
        setFormValues(prev => ({ ...prev, [`${groupSlug}.${fieldSlug}`]: value }));
    };

    const handleFileChange = (groupSlug, fieldSlug, file) => {
        if (isReadOnly) return;
        if (file) {
            setFiles(prev => ({ ...prev, [`${groupSlug}.${fieldSlug}`]: file }));
            setFormValues(prev => ({ ...prev, [`${groupSlug}.${fieldSlug}`]: file.name }));
            if (file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onloadend = () => setFilePreviews(prev => ({ ...prev, [`${groupSlug}.${fieldSlug}`]: reader.result }));
                reader.readAsDataURL(file);
            }
        }
    };

    const handleNextStep = () => {
        if (currentStep < totalSteps - 1) {
            setCompletedSteps(prev => [...new Set([...prev, currentStep])]);
            setCurrentStep(prev => prev + 1);
        }
    };

    const handlePreviousStep = () => { if (currentStep > 0) setCurrentStep(prev => prev - 1); };

    const handleSave = () => {
        setCompletedSteps(prev => [...new Set([...prev, currentStep])]);
        saveToLocalStorage();
    };

    const handleReset = () => {
        if (window.confirm(language === 'fa' ? 'آیا از حذف اطلاعات ذخیره شده اطمینان دارید؟' : 'Are you sure?')) {
            localStorage.removeItem(storageKey);
            setFormValues({}); setFilePreviews({}); setFiles({}); setCompletedSteps([]); setCurrentStep(0);
        }
    };

    const handleStepClick = (stepIndex) => {
        if (completedSteps.includes(stepIndex) || stepIndex === currentStep) setCurrentStep(stepIndex);
    };

    const getStepStatus = (stepIndex) => {
        if (completedSteps.includes(stepIndex)) return 'completed';
        if (stepIndex === currentStep) return 'current';
        return 'pending';
    };

    const renderField = (field, groupSlug) => {
        const fieldKey = `${groupSlug}.${field.slug}`;
        const value = formValues[fieldKey] || '';
        const disabled = isReadOnly;

        switch (field.type) {
            case 0:
                if (field.restriction) {
                    return (
                        <div className="space-y-1.5">
                            <label className="block text-[#204286] font-bold text-xs sm:text-sm">{field.title}{field.required && <span className="text-red-500 mr-1">*</span>}</label>
                            <select value={value} onChange={(e) => handleInputChange(groupSlug, field.slug, e.target.value)} disabled={disabled}
                                className="w-full px-3 py-2 sm:px-4 sm:py-3 rounded-xl border-2 border-[#EBEBEB] focus:border-[#FFCD7C] focus:outline-none transition-colors bg-white text-[#204286] font-medium text-sm disabled:bg-gray-100 disabled:cursor-not-allowed">
                                <option value="">{language === 'fa' ? 'انتخاب کنید...' : 'Select...'}</option>
                                {field.restriction.choices?.map((choice) => <option key={choice.slug} value={choice.slug}>{choice.title}</option>)}
                            </select>
                        </div>
                    );
                }
                return (
                    <div className="space-y-1.5">
                        <label className="block text-[#204286] font-bold text-xs sm:text-sm">{field.title}{field.required && <span className="text-red-500 mr-1">*</span>}</label>
                        <input type="text" value={value} onChange={(e) => handleInputChange(groupSlug, field.slug, e.target.value)} disabled={disabled} placeholder={field.title}
                            className="w-full px-3 py-2 sm:px-4 sm:py-3 rounded-xl border-2 border-[#EBEBEB] focus:border-[#FFCD7C] focus:outline-none transition-colors text-[#204286] font-medium placeholder:text-gray-400 text-sm disabled:bg-gray-100 disabled:cursor-not-allowed" />
                    </div>
                );
            case 1:
                return (
                    <div className="space-y-1.5">
                        <label className="block text-[#204286] font-bold text-xs sm:text-sm">{field.title}{field.required && <span className="text-red-500 mr-1">*</span>}</label>
                        <input type="number" value={value} onChange={(e) => handleInputChange(groupSlug, field.slug, e.target.value)} disabled={disabled} placeholder={field.title}
                            className="w-full px-3 py-2 sm:px-4 sm:py-3 rounded-xl border-2 border-[#EBEBEB] focus:border-[#FFCD7C] focus:outline-none transition-colors text-[#204286] font-medium placeholder:text-gray-400 text-sm disabled:bg-gray-100 disabled:cursor-not-allowed" />
                    </div>
                );
            case 2:
                return (
                    <div className="space-y-1.5">
                        <label className="block text-[#204286] font-bold text-xs sm:text-sm">{field.title}{field.required && <span className="text-red-500 mr-1">*</span>}</label>
                        <div className="relative">
                            {!isReadOnly && (
                                <input type="file" onChange={(e) => handleFileChange(groupSlug, field.slug, e.target.files[0])}
                                    className="w-full px-3 py-2 sm:px-4 sm:py-3 rounded-xl border-2 border-dashed border-[#EBEBEB] focus:border-[#FFCD7C] focus:outline-none transition-colors text-[#204286] text-xs sm:text-sm file:mr-2 sm:file:mr-4 file:py-1.5 file:px-3 sm:file:py-2 sm:file:px-4 file:rounded-lg file:border-0 file:bg-[#FFCD7C] file:text-[#204286] file:font-bold file:text-xs sm:file:text-sm hover:file:bg-[#f5b856]" />
                            )}
                            {filePreviews[fieldKey] && (
                                <motion.img src={filePreviews[fieldKey]} alt="Preview" className="mt-2 rounded-lg max-h-32 object-cover" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} onError={(e) => { e.target.style.display = 'none'; }} />
                            )}
                            {!filePreviews[fieldKey] && formValues[fieldKey] && (
                                <p className="text-xs text-gray-500 mt-1">📎 {formValues[fieldKey]}</p>
                            )}
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    // Loading
    if (loading) {
        return (
            <div className="max-w-[1136px] mx-auto px-4 py-8 font-iransansRegular">
                <div className="flex justify-center items-center py-20">
                    <div className="flex flex-col items-center gap-4">
                        <div className="w-12 h-12 border-4 border-[#292DD8] border-t-transparent rounded-full animate-spin"></div>
                        <p className="text-[#292DD8] font-bold text-lg">{language === 'fa' ? 'در حال بارگذاری...' : 'Loading...'}</p>
                    </div>
                </div>
            </div>
        );
    }

    // Error
    if (error) {
        return (
            <div className="max-w-[1136px] mx-auto px-4 py-8 font-iransansRegular">
                <div className="flex justify-center items-center py-20">
                    <div className="flex flex-col items-center gap-4 bg-red-50 rounded-xl p-6">
                        <svg className="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                        <p className="text-red-600 font-bold text-lg">{error}</p>
                        <button onClick={fetchFormData} className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors">{language === 'fa' ? 'تلاش مجدد' : 'Try Again'}</button>
                    </div>
                </div>
            </div>
        );
    }

    const currentGroup = formData?.groups?.[currentStep];
    const allFilled = areAllRequiredFieldsFilled();

    return (
        <div className="max-w-[1136px] mx-auto px-3 sm:px-4 py-3 lg:py-8 font-iransansRegular overflow-hidden">
            <div className="lg:hidden mb-3">
                <MessagesSidebar
                    activeKey=""
                    onNavigate={(key) => {
                        if (key === 'messages') navigate('/messages');
                        if (key === 'finance') navigate('/finance');
                        if (key === 'account') navigate('/account');
                        if (key === 'support') navigate('/support');
                        if (key === 'logout') navigate('/logout');
                    }}
                />
            </div>

            <div className="flex gap-6">
                <div className="hidden lg:block w-[272px] shrink-0">
                    <MessagesSidebar
                    activeKey=""
                    onNavigate={(key) => {
                        if (key === 'messages') navigate('/messages');
                        if (key === 'finance') navigate('/finance');
                        if (key === 'account') navigate('/account');
                        if (key === 'support') navigate('/support');
                        if (key === 'logout') navigate('/logout');
                    }}
                />
                </div>

                <div className="flex-1 min-w-0">
                    <motion.h1 className="text-xl sm:text-2xl lg:text-[32px] font-extrabold text-[#292DD8] mb-3 lg:mb-4" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
                        {isReadOnly
                            ? (language === 'fa' ? 'مشاهده اطلاعات' : 'View Information')
                            : isEditMode
                                ? (language === 'fa' ? 'ویرایش اطلاعات' : 'Edit Information')
                                : (language === 'fa' ? 'ثبت نام اولیه' : 'Initial Registration')
                        }
                    </motion.h1>

                    {saveMessage && <motion.div className="mb-4 px-4 py-2 bg-green-50 border border-green-200 text-green-600 rounded-xl text-sm text-center" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>{saveMessage}</motion.div>}
                    {submitError && <motion.div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 text-red-600 rounded-xl text-sm" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>{submitError}</motion.div>}
                    {submitSuccess && <motion.div className="mb-4 px-4 py-3 bg-green-50 border border-green-200 text-green-600 rounded-xl text-sm text-center" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}><p className="font-bold">{submitSuccess}</p><p className="text-xs mt-1">{language === 'fa' ? 'در حال انتقال...' : 'Redirecting...'}</p></motion.div>}

                    <motion.div className="mb-4 lg:mb-8" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
                        <div className="flex items-center gap-2 mb-1.5">
                            <div className="flex-1 flex items-center gap-2">
                                <span className="text-[#204286] font-extrabold text-lg sm:text-xl lg:text-2xl">{progress}%</span>
                                <span className="text-gray-500 text-xs sm:text-sm">{language === 'fa' ? 'تکمیل شده' : 'Completed'}</span>
                            </div>
                            <span className="text-xs text-gray-400">{completedSteps.length}/{totalSteps}</span>
                        </div>
                        <div className="w-full h-2 lg:h-3 bg-[#EBEBEB] rounded-full overflow-hidden">
                            <motion.div className="h-full bg-gradient-to-r from-[#FFCD7C] to-[#F7BD5F] rounded-full" initial={{ width: 0 }} animate={{ width: `${progress}%` }} transition={{ duration: 0.8, ease: "easeOut" }} />
                        </div>
                    </motion.div>

                    <div className="flex items-center justify-center mb-4 lg:mb-12 gap-0.5 sm:gap-1">
                        {formData?.groups?.map((group, index) => (
                            <div key={group.slug} className="flex items-center flex-shrink-0">
                                <motion.div onClick={() => handleStepClick(index)} className={`relative w-10 h-10 sm:w-14 sm:h-14 lg:w-20 lg:h-20 rounded-full flex items-center justify-center transition-all duration-500 cursor-pointer ${getStepStatus(index) === 'completed' ? 'bg-[#FFCD7C] shadow-lg shadow-[#FFCD7C]/30' : getStepStatus(index) === 'current' ? 'border-3 sm:border-4 border-[#FFCD7C] bg-white shadow-lg' : 'border-3 sm:border-4 border-[#EBEBEB] bg-white'}`} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                    {getStepStatus(index) === 'completed' && <motion.div initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 300, damping: 15 }} className="w-full h-full flex items-center justify-center"><img src="/white-sign.png" alt="Completed" className="w-5 h-5 sm:w-7 sm:h-7 lg:w-10 lg:h-10 object-contain" /></motion.div>}
                                    {getStepStatus(index) === 'current' && <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 300, damping: 15 }} className="w-full h-full flex items-center justify-center"><img src="/black-sign.png" alt="Current" className="w-5 h-5 sm:w-7 sm:h-7 lg:w-10 lg:h-10 object-contain" /></motion.div>}
                                    {getStepStatus(index) === 'pending' && <div className="w-full h-full flex items-center justify-center"><img src="/grey-sign.png" alt="Pending" className="w-5 h-5 sm:w-7 sm:h-7 lg:w-10 lg:h-10 object-contain opacity-50" /></div>}
                                </motion.div>
                                {index < totalSteps - 1 && <motion.div className={`w-4 sm:w-8 lg:w-24 h-1 lg:h-1.5 rounded-full transition-all duration-500 ${getStepStatus(index) === 'completed' ? 'bg-[#FFCD7C]' : 'bg-[#EBEBEB]'}`} initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.5, delay: index * 0.2 }} />}
                            </div>
                        ))}
                    </div>

                    <AnimatePresence mode="wait">
                        <motion.div key={currentStep} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3 }} className="bg-white rounded-2xl border-2 border-[#EBEBEB] p-3 sm:p-4 lg:p-8 shadow-lg">
                            <div className="flex items-center gap-2 sm:gap-3 mb-4 lg:mb-8">
                                <div className="w-7 h-7 sm:w-10 sm:h-10 rounded-full bg-[#FFCD7C]/20 flex items-center justify-center flex-shrink-0"><span className="text-[#FFCD7C] font-bold text-xs sm:text-sm">{currentStep + 1}</span></div>
                                <h2 className="text-sm sm:text-base lg:text-xl font-extrabold text-[#204286] truncate">{currentGroup?.title}</h2>
                            </div>
                            <div className="space-y-3 sm:space-y-4 lg:space-y-6">
                                {currentGroup?.fields?.map((field) => (
                                    <motion.div key={field.slug} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>{renderField(field, currentGroup.slug)}</motion.div>
                                ))}
                            </div>
                        </motion.div>
                    </AnimatePresence>

                    {/* Normal/Edit mode butonları */}
                    {!isReadOnly && (
                        <div className="flex flex-col sm:flex-row justify-center items-stretch sm:items-center gap-2 sm:gap-3 mt-4 lg:mt-8">
                            <motion.button onClick={handlePreviousStep} disabled={currentStep === 0} whileHover={currentStep !== 0 ? { scale: 1.02 } : {}} whileTap={currentStep !== 0 ? { scale: 0.98 } : {}} className={`px-4 py-2.5 sm:py-3 rounded-xl font-bold text-xs sm:text-sm transition-all duration-300 flex items-center justify-center gap-1.5 order-2 sm:order-1 ${currentStep === 0 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-[#EBEBEB] text-[#204286] hover:bg-gray-300 hover:shadow-lg'}`}>
                                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                                {language === 'fa' ? 'مرحله قبل' : 'Prev'}
                            </motion.button>
                            <motion.button onClick={handleSave} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="px-6 py-2.5 sm:py-3 rounded-xl font-bold text-xs sm:text-sm transition-all duration-300 order-1 sm:order-2 bg-[#204286] text-white shadow-lg hover:shadow-xl flex items-center justify-center gap-1.5">
                                {language === 'fa' ? 'ذخیره اطلاعات' : 'Save'}
                                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" /></svg>
                            </motion.button>
                            {isLastStep && allFilled ? (
                                <motion.button onClick={handleSubmit} disabled={submitting} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="px-6 py-2.5 sm:py-3 rounded-xl font-bold text-xs sm:text-sm transition-all duration-300 order-3 bg-green-500 text-white hover:bg-green-600 hover:shadow-lg flex items-center justify-center gap-1.5 disabled:opacity-50">
                                    {submitting ? <span className="flex items-center gap-2"><svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>{language === 'fa' ? 'در حال ارسال...' : 'Submitting...'}</span> : <>{isEditMode ? (language === 'fa' ? 'بروزرسانی اطلاعات' : 'Update Information') : (language === 'fa' ? 'ارسال اطلاعات' : 'Submit')}<svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg></>}
                                </motion.button>
                            ) : (
                                <motion.button onClick={handleNextStep} disabled={currentStep === totalSteps - 1} whileHover={currentStep !== totalSteps - 1 ? { scale: 1.02 } : {}} whileTap={currentStep !== totalSteps - 1 ? { scale: 0.98 } : {}} className={`px-4 py-2.5 sm:py-3 rounded-xl font-bold text-xs sm:text-sm transition-all duration-300 flex items-center justify-center gap-1.5 order-3 ${currentStep === totalSteps - 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-[#FFB947] text-white hover:shadow-lg'}`}>
                                    {language === 'fa' ? 'مرحله بعد' : 'Next'}
                                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                </motion.button>
                            )}
                        </div>
                    )}

                    {/* View mode - sadece gezinti butonları */}
                    {isReadOnly && (
                        <div className="flex justify-center items-center gap-2 sm:gap-3 mt-4 lg:mt-8">
                            <motion.button onClick={handlePreviousStep} disabled={currentStep === 0} whileHover={currentStep !== 0 ? { scale: 1.02 } : {}} whileTap={currentStep !== 0 ? { scale: 0.98 } : {}} className={`px-4 py-2.5 sm:py-3 rounded-xl font-bold text-xs sm:text-sm transition-all duration-300 flex items-center justify-center gap-1.5 ${currentStep === 0 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-[#EBEBEB] text-[#204286] hover:bg-gray-300 hover:shadow-lg'}`}>
                                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                                {language === 'fa' ? 'مرحله قبل' : 'Prev'}
                            </motion.button>
                            <motion.button onClick={handleNextStep} disabled={currentStep === totalSteps - 1} whileHover={currentStep !== totalSteps - 1 ? { scale: 1.02 } : {}} whileTap={currentStep !== totalSteps - 1 ? { scale: 0.98 } : {}} className={`px-4 py-2.5 sm:py-3 rounded-xl font-bold text-xs sm:text-sm transition-all duration-300 flex items-center justify-center gap-1.5 ${currentStep === totalSteps - 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-[#FFB947] text-white hover:shadow-lg'}`}>
                                {language === 'fa' ? 'مرحله بعد' : 'Next'}
                                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                            </motion.button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// MobileMenu component


export default EventForm;